/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect } from 'react'
import { Collapse } from 'react-bootstrap'
import { Formik, Form } from 'formik'
import _ from 'lodash'
import { useServiceContext } from '../../../root'
import { useRcmTableData } from '../../../hooks/useRcmTableData'
import { InfiniteScrollSelect } from '../../../components/InfiniteScrollSelect'
import { folderStructureTemplateTypesApiService } from '../../../services/api/api.folderstructuretemplatetypes'

const persistanceKey = 'settingsFolderStrTplTypesFilter'

interface FilterFormValues {
  types: Array<{ value: number; label: string }>
}

const TableFilter: React.FC = () => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const tableData = useRcmTableData()

  const getDefaultFilter = (): FilterFormValues => ({
    types: []
  })

  const [formValues, setFormValues] = useState<FilterFormValues>(() => {
    const persisted = context.service.persist.getGlobal(persistanceKey) as FilterFormValues | null
    return persisted || getDefaultFilter()
  })

  useEffect(() => {
    const persisted = context.service.persist.getGlobal(persistanceKey) as FilterFormValues | null
    if (persisted && !_.isEqual(persisted, getDefaultFilter())) {
      setFormValues(persisted)
      tableData.applyFilter(persisted)
    }
  }, [])

  const applyFilter = (filter: FilterFormValues) => {
    context.service.persist.setGlobal(persistanceKey, filter)
    tableData.applyFilter(filter)
  }

  const handleApplyFilter = () => {
    applyFilter(formValues)
  }

  const handleClearFilter = () => {
    const defaultFilter = getDefaultFilter()
    setFormValues(defaultFilter)
    applyFilter(defaultFilter)
  }

  const loadOptions = async (search: string, page: number) => {
    return await folderStructureTemplateTypesApiService.getOptionsForAsyncPicker(search, page)
  }

  return (
    <Collapse in={!tableData.isFilterCollapsed}>
      <div>
        <div className="filter-wrapper">
          <div className="row">
            <div className="col-sm-6 col-lg-4">
              <Formik initialValues={formValues} onSubmit={handleApplyFilter} enableReinitialize>
                {({ values, setFieldValue }) => (
                  <Form>
                    <div className="form-group">
                      <label className="control-label">{translate('FOLDER_STRUCTURE_TEMPLATE_TYPES_TITLE')}</label>
                      <InfiniteScrollSelect
                        placeholder={translate('TYPES...')}
                        name="types"
                        value={values.types}
                        asyncLoadFn={loadOptions}
                        onChange={(option) => {
                          const types = (option || []) as Array<{ value: number; label: string }>
                          setFieldValue('types', types)
                          setFormValues({ ...values, types })
                        }}
                        isMulti
                        searchable
                        id="4c83da88-d4a9-4d43-ab73-f18f32818968"
                      />
                    </div>
                    <div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleApplyFilter}
                        id="550bf54b-4272-4cc3-b7b4-636d118aaadd"
                      >
                        {translate('BTN_FILTER')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-link"
                        onClick={handleClearFilter}
                        disabled={_.isEqual(getDefaultFilter().types, values.types)}
                        id="96f8d6b0-e435-44d1-87dd-baab9cbcec25"
                      >
                        {translate('BTN_CLEAR_FILTER')}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </div>
      </div>
    </Collapse>
  )
}

export default TableFilter
